import { CartItem, ImageAsset, Product } from '@/models/ProductTypes';
import { colorToCss, formatISK, normHex } from '@/utils/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useAtom, useSetAtom } from 'jotai';
import { cartAtom, changeCartItemAtom } from '@/store';
import { Check } from 'components/src/svg';
import { AnimatePresence, motion } from 'framer-motion';

const VariantPicker = ({
  product,
  initialSku,
  onSkuChange,
  skuImageMap,
  onPriceChange,
}: {
  product: Product;
  initialSku?: string;
  onSkuChange: (sku: string) => void;
  skuImageMap: Record<string, ImageAsset[]>;
  onPriceChange?: (price: number, discountPrice: number | null | undefined) => void;
}) => {
  const router = useRouter();
  const [cart] = useAtom(cartAtom);
  const changeCartItem = useSetAtom(changeCartItemAtom);
  const colors = useMemo(() => product?.productVariants?.variants || [], [product]);
  // A valid initialSku (from ?sku=) wins over the product's default color/storage.
  const initialSkuColorIdx = initialSku
    ? colors.findIndex((c) => c.subVariants?.variants?.some((v) => v.variantProductId === initialSku))
    : -1;
  const defaultColorIdx = Math.max(
    colors.findIndex((color) => {
      const name = color.variantName;
      const prop = color.variantProperty;
      return name === product.productCategory || normHex(prop) === normHex(product.productColorCode);
    }),
    0
  );
  const preferredColorIdx = initialSkuColorIdx >= 0 ? initialSkuColorIdx : defaultColorIdx;
  const [colorIdx, setColorIdx] = useState<number>(preferredColorIdx);
  const [activeAvailability, setActiveAvailability] = useState<boolean>(false);
  const [activePrice, setActivePrice] = useState<number>(product.productPrice);
  const [activeDiscountPrice, setActiveDiscountPrice] = useState<number | null | undefined>(
    product.productDiscountPrice
  );

  const storages = useMemo(() => {
    return colors.length > 0 ? colors[colorIdx].subVariants?.variants : [];
  }, [colorIdx, colors]);

  const preferredStorageIdx = Math.max(
    storages.findIndex((s) => s.variantProductId === (initialSkuColorIdx >= 0 ? initialSku : product.productId)),
    0
  );
  const [storageIdx, setStorageIdx] = useState<number>(preferredStorageIdx);

  const activeSku = useMemo(() => {
    const selectedStorage = storages[storageIdx];
    return selectedStorage?.variantProductId ?? product.productId ?? '';
  }, [storageIdx, storages, product]);

  const [isAnimating, setIsAnimating] = useState(false);
  const [animationStart, setAnimationStart] = useState({ x: 0, y: 0 });
  const [cartPosition, setCartPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    onSkuChange(activeSku);
  }, [activeSku, onSkuChange]);

  // Keep ?sku= in the address bar in sync with the selection so the URL is always shareable.
  const defaultSku = useRef(activeSku);
  useEffect(() => {
    if (!router.isReady || !activeSku) return;
    const urlSku = typeof router.query.sku === 'string' ? router.query.sku : undefined;
    if (urlSku === activeSku) return;
    // Don't add ?sku= on first load when nothing was picked and the default is showing.
    if (!urlSku && activeSku === defaultSku.current) return;
    router.replace({ pathname: router.pathname, query: { ...router.query, sku: activeSku } }, undefined, {
      shallow: true,
      scroll: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSku, router.isReady]);

  useEffect(() => {
    const newList = colors.length > 0 ? colors[colorIdx].subVariants.variants : [];
    const currentCap = storages.length > storageIdx ? storages[storageIdx].variantProperty : null;
    const sameCapIdx = newList.findIndex((v) => v.variantProperty === currentCap);
    setStorageIdx(sameCapIdx >= 0 ? sameCapIdx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorIdx, colors]);

  useEffect(() => {
    // Price & availability using tolerant helpers
    if (storages.length === 0) {
      return;
    }
    const newPrice = storages[storageIdx]?.variantProductPrice;
    const newDiscountPrice = storages[storageIdx]?.variantProductDiscountPrice;
    setActivePrice(newPrice);
    setActiveDiscountPrice(newDiscountPrice);

    if (onPriceChange) {
      onPriceChange(newPrice, newDiscountPrice);
    }
  }, [storages, storageIdx, setActivePrice, setActiveDiscountPrice, onPriceChange]);

  let _currentAvailability = 0;

  if (storages[storageIdx]?.variantAvailability || product.productAvailability) {
    _currentAvailability = storages[storageIdx]?.variantAvailability ?? product.productAvailability;
  }

  useEffect(() => {
    const reservedQuantity = cart[activeSku]?.quantity | 0;
    if (_currentAvailability !== undefined && _currentAvailability !== null) {
      setActiveAvailability(_currentAvailability - reservedQuantity > 0);
    }
  }, [_currentAvailability, activeSku, cart]);

  const priceDisplay = formatISK(activePrice);
  const activeDiscountDisplay = activeDiscountPrice ? formatISK(activeDiscountPrice) : null;

  const addToCart = () => {
    if (isAnimating) return;
    const cartItem: CartItem = {
      product: {
        id: activeSku,
        name: product.productName,
        variant: colors[colorIdx]?.variantName,
        subvariant: storages[storageIdx]?.variantProperty,
        imageUrl: skuImageMap && skuImageMap[activeSku]?.length > 0 ? skuImageMap[activeSku][0]?.url : undefined,
        price: activePrice,
      },
      quantity: 1,
    };
    if (cart[activeSku]) {
      cartItem.quantity += cart[activeSku].quantity;
    }

    const cardElement = document.getElementById('addToCartButton');

    if (cardElement) {
      const buttonRect = cardElement.getBoundingClientRect();
      const buttonX = buttonRect.left + buttonRect.width / 2;
      const buttonY = buttonRect.top + buttonRect.height / 2;

      setAnimationStart({ x: buttonX, y: buttonY });
      const cartElement = document.getElementById('store-cart-position');

      if (cartElement) {
        const cartRect = cartElement.getBoundingClientRect();
        setCartPosition({
          x: cartRect.left + cartRect.width / 2,
          y: cartRect.top + cartRect.height / 2,
        });
      }
    }

    setIsAnimating(true);

    setTimeout(() => {
      changeCartItem(cartItem);
      setIsAnimating(false);
    }, 500);

    const cartEvent = new CustomEvent('addToStoreCart', { detail: { productId: Date.now() } });
    document.dispatchEvent(cartEvent);
  };

  return (
    <div className="mb-24 flex flex-col gap-24">
      {storages.length > 0 && (
        <div className="mt-6 flex flex-col gap-12">
          <div className="flex flex-wrap gap-4 gap-y-8">
            {storages.map((storage, idx) => (
              <button
                key={`${storage.variantProductId ?? idx}`}
                type="button"
                onClick={() => setStorageIdx(idx)}
                className={`mx-4 flex h-[40px] min-w-[100px] items-center justify-center gap-4 rounded-full px-8 py-4 text-base font-medium shadow-sm transition-all md:text-sm
                  ${
                    idx === storageIdx
                      ? 'border bg-button-secondary-default text-white-100'
                      : 'bg-white text-black border-neutral-300 hover:border-black border'
                  }`}
                aria-pressed={idx === storageIdx}
              >
                {idx === storageIdx && <Check color={'#FFFFFF'} />}
                {storage.variantProperty ?? `Valkostur ${idx + 1}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {colors.length > 1 && (
        <div className="mt-6 flex flex-col">
          <p className="mb-16 text-md">Litur</p>
          <div className="mt-2 flex flex-wrap gap-8">
            {colors.map((color, idx) => {
              const bg = colorToCss(color.variantProperty);
              return (
                <button
                  key={`${color.variantName ?? `Litur ${idx + 1}`}-${idx}`}
                  type="button"
                  onClick={() => setColorIdx(idx)}
                  className={`relative mx-2 h-[24px] w-[24px] rounded-full border px-4 text-sm transition
                    ${idx === colorIdx ? 'border-black ring-2 ring-primary-red' : 'border-primary-faded-text'}`}
                  aria-pressed={idx === colorIdx}
                  title={color.variantName ?? `Litur ${idx + 1}`}
                  style={bg ? { backgroundColor: bg, color: '#fff' } : undefined}
                >
                  {!bg && <span className="bg-neutral-500 mr-2 inline-block h-4 w-4 rounded-full align-middle" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex text-md text-primary-red">
        <div className="flex flex-col">
          <span className="text-md">Verð:</span>
          <span className="text-[32px] font-bold">{priceDisplay}</span>
          {activeDiscountDisplay && (
            <div className="flex items-center gap-8">
              <span className="text-md line-through">{activeDiscountDisplay}</span>
              {activeDiscountPrice && activePrice && (
                <span className="ml-4 rounded-8 bg-primary-sea px-8 py-4 text-sm font-semibold text-white-50">
                  -{Math.floor(((activeDiscountPrice - activePrice) / activeDiscountPrice) * 100)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <button
          id="addToCartButton"
          type="button"
          disabled={!activeAvailability}
          onClick={activeAvailability ? addToCart : undefined}
          className={`shadow w-full rounded-full py-16 text-md font-semibold transition-all md:w-[300px]
            ${
              activeAvailability
                ? 'hover:bg-red-60 cursor-pointer bg-primary-red text-white-100'
                : 'cursor-not-allowed bg-white-5 text-grey-100 opacity-70'
            }`}
        >
          {activeAvailability ? 'Bæta í körfu' : 'Vara Uppseld'}
        </button>
      </div>
      <hr className="text-[#E1E2E3]" />
      <div className="flex flex-col">
        <h4 className="mb-8">Birgðastaða</h4>
        <div className="w-fit rounded-full bg-white-15 py-4 pl-4 pr-16">
          {activeAvailability ? (
            <span className="flex">
              <span className="mr-4 flex h-24 w-24 justify-center rounded-full bg-[#008F23] text-white-100">✓</span>Til
              á lager
            </span>
          ) : (
            <span className="ml-8">
              <span className="h-24 w-24">❌</span> Uppselt
            </span>
          )}
        </div>
      </div>
      <AnimatePresence>
        {isAnimating && (
          // eslint-disable-next-line react/jsx-no-undef
          <motion.div
            className="card-elevation-1 fixed z-[1000] flex h-32 w-32 rounded-full bg-red-100"
            style={{
              top: 0,
              left: 0,
              marginLeft: '-12px',
              marginTop: '-12px',
            }}
            initial={{
              x: animationStart.x,
              y: animationStart.y,
              scale: 0,
            }}
            animate={{
              x: [animationStart.x, animationStart.x, cartPosition.x, cartPosition.x],
              y: [animationStart.y, animationStart.y, cartPosition.y, cartPosition.y],
              scale: [0, 1.2, 1, 0],
            }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default VariantPicker;
